import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Divider,
  useTheme,
  Switch,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Menu,
  Snackbar,
  Alert,
  Paper,
} from "@mui/material";
import TranslateIcon from "@mui/icons-material/Translate";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import axios from "axios";
import dayjs from "dayjs";
import MedalDialog from "../Components/MedalDialog";

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const ProfileMedals = () => {
  const theme = useTheme();
  const [language, setLanguage] = useState("de");
  const [catalog, setCatalog] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMedal, setEditingMedal] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("current");
  const [compareMonth, setCompareMonth] = useState("");
  const [compareValues, setCompareValues] = useState({});
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [username, setUsername] = useState("");

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const showSnack = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const currentMonth = dayjs().format("YYYY-MM");
  const isCurrentMonthSelected = selectedMonth === "current";
  const showOnlyCurrent = isCurrentMonthSelected && !compareMonth;
  const showOnlySelected = !isCurrentMonthSelected && !compareMonth;
  const showComparison = compareMonth !== "";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catalogRes, settingsRes, monthsRes, meRes] = await Promise.all([
          axios.get("/api/users/medals/catalog"),
          axios.get("/api/users/settings", { headers: authHeader() }),
          axios.get("/api/users/medals/months", { headers: authHeader() }),
          axios.get("/api/users/me", { headers: authHeader() }),
        ]);
        setCatalog(catalogRes.data);
        setIsAdmin(settingsRes.data?.isAdmin === true);
        setAvailableMonths(monthsRes.data || []);
        setUsername(meRes.data?.username || "");
      } catch (error) {
        console.error("Fehler beim Laden:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchMedals = async () => {
      const effectiveMonth =
        selectedMonth === "current" ? currentMonth : selectedMonth;
      if (!effectiveMonth) return;
      try {
        const res = await axios.get(
          `/api/users/medals/by-month?month=${effectiveMonth}`,
          { headers: authHeader() }
        );
        setValues(res.data?.medal_values || {});
      } catch (err) {
        console.error("Fehler beim Laden der Monatsmedaillen:", err);
      }
    };
    fetchMedals();
  }, [selectedMonth, currentMonth]);

  useEffect(() => {
    const fetchComparison = async () => {
      if (!compareMonth) return setCompareValues({});
      try {
        const res = await axios.get(
          `/api/users/medals/by-month?month=${compareMonth}`,
          { headers: authHeader() }
        );
        setCompareValues(res.data?.medal_values || {});
      } catch (err) {
        console.error("Fehler beim Vergleichsmonat:", err);
      }
    };
    fetchComparison();
  }, [compareMonth]);

  const handleChange = (id, newValue) => {
    setValues((prev) => ({ ...prev, [id]: parseInt(newValue) || 0 }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.post(
        "/api/users/medals",
        { month: currentMonth, medal_values: values },
        { headers: authHeader() }
      );
      showSnack("Medaillen gespeichert.");
    } catch {
      showSnack("Fehler beim Speichern.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDialogClose = (updated) => {
    setDialogOpen(false);
    setEditingMedal(null);
    if (updated) window.location.reload();
  };

  const handleSubmitMedal = async (data) => {
    try {
      if (editingMedal) {
        await axios.patch(
          `/api/users/medals/catalog/${editingMedal.id}`,
          data,
          { headers: authHeader() }
        );
      } else {
        await axios.post("/api/users/medals/catalog", data, {
          headers: authHeader(),
        });
      }
      showSnack("Medaille gespeichert.");
      handleDialogClose(true);
    } catch {
      showSnack("Fehler beim Speichern der Medaille.", "error");
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        px: 2,
        pt: 3,
        pb: { xs: "88px", sm: "72px" },
        maxWidth: 600,
        mx: "auto",
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <Typography variant="h5">Medaillen von {username}</Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <FormControlLabel
            control={
              <Switch
                checked={language === "en"}
                onChange={() =>
                  setLanguage((prev) => (prev === "de" ? "en" : "de"))
                }
                size="small"
              />
            }
            label={<TranslateIcon fontSize="small" />}
            sx={{ mr: 0 }}
          />
          {isAdmin && (
            <>
              <IconButton
                size="small"
                onClick={(e) => setMenuAnchorEl(e.currentTarget)}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={() => setMenuAnchorEl(null)}
              >
                <MenuItem
                  onClick={() => {
                    setEditMode((p) => !p);
                    setMenuAnchorEl(null);
                  }}
                >
                  {editMode ? "Bearbeiten beenden" : "Medaillen bearbeiten"}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setEditingMedal(null);
                    setDialogOpen(true);
                    setMenuAnchorEl(null);
                  }}
                >
                  Neue Medaille
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Box>

      {/* Monat-Auswahl */}
      <Box display="flex" gap={1.5} mb={2} flexWrap="wrap">
        <Select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          size="small"
          sx={{ flex: 1, minWidth: 140 }}
        >
          <MenuItem value="current">Aktueller Monat</MenuItem>
          {availableMonths.map((month) => (
            <MenuItem key={month} value={month}>
              {month}
            </MenuItem>
          ))}
        </Select>
        <Select
          value={compareMonth}
          onChange={(e) => setCompareMonth(e.target.value)}
          displayEmpty
          size="small"
          sx={{ flex: 1, minWidth: 140 }}
          renderValue={(selected) =>
            selected === "" ? (
              <Typography color="text.secondary" variant="body2">
                Vergleichen mit…
              </Typography>
            ) : (
              selected
            )
          }
        >
          <MenuItem value="">
            <em>Kein Vergleich</em>
          </MenuItem>
          {availableMonths.map((month) => (
            <MenuItem key={month} value={month}>
              {month}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Medaillen-Liste */}
      {catalog
        .filter(
          (medal) =>
            showOnlyCurrent ||
            (showOnlySelected && values[medal.id]) ||
            (showComparison && (values[medal.id] || compareValues[medal.id]))
        )
        .map((medal) => (
          <Paper
            key={medal.id}
            elevation={0}
            sx={{
              mb: 2,
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              position: "relative",
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              {language === "de" ? medal.title_de : medal.title_en}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1.5}>
              {language === "de" ? medal.description_de : medal.description_en}
            </Typography>

            {showOnlyCurrent && (
              <TextField
                type="number"
                value={values[medal.id] || ""}
                onChange={(e) => handleChange(medal.id, e.target.value)}
                fullWidth
                size="small"
                inputProps={{ min: 0 }}
              />
            )}
            {showOnlySelected && (
              <Typography variant="body1">
                {selectedMonth}:{" "}
                <strong>{values[medal.id] || 0}</strong>
              </Typography>
            )}
            {showComparison && (
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">
                  {selectedMonth === "current" ? currentMonth : selectedMonth}:{" "}
                  <strong>{values[medal.id] || 0}</strong>
                </Typography>
                <Typography variant="body2">
                  {compareMonth}: <strong>{compareValues[medal.id] || 0}</strong>
                </Typography>
              </Box>
            )}

            {editMode && (
              <IconButton
                size="small"
                onClick={() => {
                  setEditingMedal(medal);
                  setDialogOpen(true);
                }}
                sx={{ position: "absolute", top: 8, right: 8 }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Paper>
        ))}

      {(!isCurrentMonthSelected || compareMonth) && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          align="center"
          mt={1}
          mb={2}
        >
          {compareMonth
            ? "Während eines Vergleichs können Medaillen nicht bearbeitet werden."
            : "Nur der aktuelle Monat kann bearbeitet werden."}
        </Typography>
      )}

      <Box textAlign="center" mt={2}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSave}
          disabled={saving || !isCurrentMonthSelected || compareMonth !== ""}
        >
          {saving ? "Speichern…" : "Speichern"}
        </Button>
      </Box>

      <MedalDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleSubmitMedal}
        initialValues={editingMedal}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfileMedals;
